import { useEffect, useState } from 'react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@journ/ui/accordion'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { ScrollArea } from '@journ/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { Sheet, SheetContent, SheetHeader } from '@journ/ui/sheet'
import { SubmitButton } from '@journ/ui/submit-button'
import { Textarea } from '@journ/ui/textarea'
import { useIntlayer } from 'react-intlayer'
import { useSelector } from '@/libs/state'
import { updateStepWithTransitions } from '../application/update-step-with-transitions'
import { activeWorkflow$, editingStepId$ } from '../store'
import { workflows as workflowsPort, notify } from '../wiring'
import type { Phase, Transition, TransitionTarget, TransitionTrigger } from '../domain/workflow'

type DraftTransition = {
	key: string
	existingId: string | null
	isDefault: boolean
	label: string
	trigger: TransitionTrigger
	targetKind: 'step' | 'phase_handoff' | 'none'
	targetStepId: string
	targetPhaseId: string
}

const buildTarget = (t: DraftTransition, phases: Phase[]): TransitionTarget => {
	if (t.targetKind === 'step') return { kind: 'step', stepId: t.targetStepId }
	if (t.targetKind === 'phase_handoff') {
		const phase = phases.find(p => p.id === t.targetPhaseId)!
		const inboxStep = phase.teamWorkflow.steps.find(s => s.isInbox)
		return {
			kind: 'phase_handoff',
			phaseId: phase.id,
			firstStepId: inboxStep?.id ?? phase.teamWorkflow.steps[0]?.id ?? '',
		}
	}
	return { kind: 'none' }
}

const existingTransitionToDraft = (t: Transition): DraftTransition => {
	let targetKind: DraftTransition['targetKind'] = 'none'
	let targetStepId = ''
	let targetPhaseId = ''

	if (t.target.kind === 'step') {
		targetKind = 'step'
		targetStepId = t.target.stepId
	} else if (t.target.kind === 'phase_handoff') {
		targetKind = 'phase_handoff'
		targetPhaseId = t.target.phaseId
	}

	return {
		key: t.id,
		existingId: t.id,
		isDefault: t.isDefault,
		label: t.label,
		trigger: t.trigger,
		targetKind,
		targetStepId,
		targetPhaseId,
	}
}

type Props = {
	onSuccess: () => void
}

const StepEditSheet = ({ onSuccess }: Props) => {
	const stepId = useSelector(editingStepId$)
	const workflow = useSelector(activeWorkflow$)
	const c = useIntlayer('workflow-editor').editStep

	const step = stepId ? (workflow?.phases.flatMap(p => p.teamWorkflow.steps).find(s => s.id === stepId) ?? null) : null

	const currentPhase = workflow?.phases.find(p => p.teamWorkflow.steps.some(s => s.id === stepId)) ?? null

	const isOpen = step !== null

	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [transitions, setTransitions] = useState<DraftTransition[]>([])
	const [openTransitions, setOpenTransitions] = useState<string[]>([])
	const [removedIds, setRemovedIds] = useState<string[]>([])
	const [submitting, setSubmitting] = useState(false)

	const phases = workflow?.phases ?? []
	const stepsInPhase = currentPhase?.teamWorkflow.steps.filter(s => !s.isInbox && s.id !== stepId) ?? []
	const otherPhases = phases.filter(p => p.id !== currentPhase?.id)

	useEffect(() => {
		if (step) {
			const drafts = step.transitions.map(existingTransitionToDraft)
			setName(step.name)
			setDescription(step.description ?? '')
			setTransitions(drafts)
			setOpenTransitions(drafts.map(d => d.key))
			setRemovedIds([])
		}
	}, [step])

	const close = () => {
		editingStepId$.set(null)
		setName('')
		setDescription('')
		setTransitions([])
		setOpenTransitions([])
		setRemovedIds([])
	}

	const addTransition = () => {
		const key = crypto.randomUUID()
		setTransitions(prev => [
			...prev,
			{
				key,
				existingId: null,
				isDefault: false,
				label: '',
				trigger: 'manual',
				targetKind: 'none',
				targetStepId: '',
				targetPhaseId: '',
			},
		])
		setOpenTransitions(prev => [...prev, key])
	}

	const removeTransition = (key: string) => {
		const t = transitions.find(d => d.key === key)
		if (!t || t.isDefault) return
		if (t.existingId) setRemovedIds(prev => [...prev, t.existingId!])
		setTransitions(prev => prev.filter(d => d.key !== key))
		setOpenTransitions(prev => prev.filter(k => k !== key))
	}

	const updateTransition = (key: string, patch: Partial<DraftTransition>) =>
		setTransitions(prev => prev.map(t => (t.key === key ? { ...t, ...patch } : t)))

	const isTransitionValid = (t: DraftTransition) => {
		if (!t.label.trim()) return false
		if (t.targetKind === 'step') return Boolean(t.targetStepId)
		if (t.targetKind === 'phase_handoff') return Boolean(t.targetPhaseId)
		return true
	}

	const canSubmit = Boolean(name.trim() && transitions.every(isTransitionValid))

	const submit = async () => {
		if (!step || !canSubmit) return

		setSubmitting(true)
		const ok = await updateStepWithTransitions(
			workflowsPort,
			{
				stepId: step.id,
				name: name.trim(),
				description: description.trim() || null,
				removedTransitionIds: removedIds,
				transitions: transitions.map(t => ({
					existingId: t.existingId,
					isDefault: t.isDefault,
					label: t.label.trim(),
					trigger: t.trigger,
					target: buildTarget(t, phases),
				})),
			},
			notify,
		)
		setSubmitting(false)
		if (ok) {
			onSuccess()
			close()
		}
	}

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		await submit()
	}

	return (
		<Sheet open={isOpen} onOpenChange={close}>
			<SheetContent stack title={c.title.value}>
				<SheetHeader className='mb-6 flex justify-between items-center flex-row'>
					<h2 className='text-xl'>{c.title}</h2>
					<Button size='icon' variant='ghost' onClick={close} className='p-0 m-0 size-auto hover:bg-transparent'>
						<Icons.Close className='size-5' />
					</Button>
				</SheetHeader>

				<ScrollArea className='h-full p-0 pb-28'>
					<form onSubmit={handleSubmit} className='flex flex-col gap-5'>
						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.nameLabel}</Label>
							<Input
								value={name}
								onChange={e => setName(e.target.value)}
								placeholder={c.namePlaceholder.value}
								autoFocus
								autoComplete='off'
							/>
						</div>

						<div className='space-y-1.5'>
							<Label className='text-xs text-878787 font-normal'>{c.descriptionLabel}</Label>
							<Textarea
								value={description}
								onChange={e => setDescription(e.target.value)}
								placeholder={c.descriptionPlaceholder.value}
								rows={3}
								className='resize-none'
							/>
						</div>

						<div className='space-y-3'>
							<Label className='text-xs text-878787 font-normal'>{c.transitionsLabel}</Label>

							{transitions.length === 0 && <p className='text-xs text-muted-foreground'>{c.noTransitions}</p>}

							<Accordion
								type='multiple'
								value={openTransitions}
								onValueChange={setOpenTransitions}
								className='space-y-0'
							>
								{transitions.map(t => (
									<AccordionItem key={t.key} value={t.key}>
										<AccordionTrigger className='py-3 text-sm font-normal'>
											<span className='flex-1 text-left truncate'>
												{t.label.trim() || <span className='text-muted-foreground italic'>{c.newTransition}</span>}
											</span>
											{!t.isDefault && (
												<Button
													type='button'
													variant='ghost'
													size='sm'
													onClick={e => {
														e.stopPropagation()
														removeTransition(t.key)
													}}
													className='shrink-0 text-destructive hover:text-destructive hover:bg-transparent px-2 mr-2 h-auto'
												>
													{c.removeTransition}
												</Button>
											)}
										</AccordionTrigger>
										<AccordionContent>
											<div className='space-y-3 pt-1'>
												<div className='space-y-1.5'>
													<Label className='text-xs text-878787 font-normal'>{c.transitionLabel}</Label>
													<Input
														value={t.label}
														onChange={e => updateTransition(t.key, { label: e.target.value })}
														placeholder={c.transitionLabelPlaceholder.value}
														autoComplete='off'
													/>
												</div>

												<div className='space-y-1.5'>
													<Label className='text-xs text-878787 font-normal'>{c.targetLabel}</Label>
													<Select
														value={t.targetKind}
														onValueChange={v =>
															updateTransition(t.key, {
																targetKind: v as DraftTransition['targetKind'],
																targetStepId: '',
																targetPhaseId: '',
															})
														}
													>
														<SelectTrigger>
															<SelectValue placeholder={c.targetPlaceholder.value} />
														</SelectTrigger>
														<SelectContent>
															<SelectItem value='none'>{c.targetNone}</SelectItem>
															{stepsInPhase.length > 0 && <SelectItem value='step'>{c.targetStep}</SelectItem>}
															{otherPhases.length > 0 && (
																<SelectItem value='phase_handoff'>{c.targetPhaseHandoff}</SelectItem>
															)}
														</SelectContent>
													</Select>
												</div>

												{t.targetKind === 'step' && (
													<div className='space-y-1.5'>
														<Label className='text-xs text-878787 font-normal'>{c.stepLabel}</Label>
														<Select
															value={t.targetStepId}
															onValueChange={v => updateTransition(t.key, { targetStepId: v })}
														>
															<SelectTrigger>
																<SelectValue placeholder={c.stepPlaceholder.value} />
															</SelectTrigger>
															<SelectContent>
																{stepsInPhase.map(s => (
																	<SelectItem key={s.id} value={s.id}>
																		{s.name}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</div>
												)}

												{t.targetKind === 'phase_handoff' && (
													<div className='space-y-1.5'>
														<Label className='text-xs text-878787 font-normal'>{c.targetPhaseLabel}</Label>
														<Select
															value={t.targetPhaseId}
															onValueChange={v => updateTransition(t.key, { targetPhaseId: v })}
														>
															<SelectTrigger>
																<SelectValue placeholder={c.targetPhasePlaceholder.value} />
															</SelectTrigger>
															<SelectContent>
																{otherPhases.map(p => (
																	<SelectItem key={p.id} value={p.id}>
																		{p.name}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</div>
												)}
											</div>
										</AccordionContent>
									</AccordionItem>
								))}
							</Accordion>

							<Button type='button' variant='outline' size='sm' onClick={addTransition} className='text-xs'>
								{c.addTransition}
							</Button>
						</div>
					</form>
				</ScrollArea>

				<div className='absolute bottom-0 left-0 right-0 p-4 bg-background border-t border-border'>
					<div className='flex justify-end space-x-4'>
						<Button variant='outline' type='button' onClick={close}>
							{c.cancel}
						</Button>
						<SubmitButton isSubmitting={submitting} disabled={!canSubmit} onClick={submit}>
							{c.save}
						</SubmitButton>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	)
}

export { StepEditSheet }
